import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { ChevronsDown, ChevronsUp } from 'lucide-react';
import type { AssessmentFormHandle } from '../assessments/NursingAssessmentForm';
import type { ClinicalTemplate } from '../../../constants/clinicalTemplates';
import TemplateSectionFields from './TemplateSectionFields';
import { ASSESSMENT_TYPE_LABELS, AssessmentTypeKey, sectionShown, templateRequiredErrors } from './templateUtils';

interface Props {
  template: ClinicalTemplate;
  editingId: string | null;
  patientId: string;
  onSave: (data: Record<string, unknown>) => void;
  existingData?: Record<string, any> | null;
  assessmentStatus?: string;
  isReadOnly?: boolean;
}

/**
 * Phase 20B: assessment from a clinical template (MSW psychosocial assessment, and future
 * disciplines). Same storage and handle as the hand-built forms: data.categories["<Section>-<Field>"],
 * data.formConfig { assessmentType, templateId, templateVersion }.
 */
const TemplateAssessmentForm = forwardRef<AssessmentFormHandle, Props>(({ template, editingId, onSave, existingData, assessmentStatus, isReadOnly = false }, ref) => {
  const types = (template.assessmentTypes || ['initial', 'recertification', 'discharge']) as AssessmentTypeKey[];
  const [categories, setCategories] = useState<Record<string, unknown>>(() => ({ ...(existingData?.categories || {}) }));
  const [assessmentType, setAssessmentType] = useState<AssessmentTypeKey>(
    () => (types.includes(existingData?.formConfig?.assessmentType) ? existingData!.formConfig.assessmentType : types[0]));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const typeLocked = isReadOnly || (!!editingId && assessmentStatus !== undefined && assessmentStatus !== 'DRAFT');

  const shownSections = useMemo(() => template.sections.filter(s => sectionShown(s, assessmentType)), [template, assessmentType]);

  const buildSaveData = () => ({
    ...(existingData || {}),
    categories,
    formConfig: { ...(existingData?.formConfig || {}), assessmentType, templateId: template.id, templateVersion: template.version },
    status: 'Draft',
  });

  const validate = (): string[] => {
    const next = templateRequiredErrors(template, categories, assessmentType);
    setErrors(next);
    return Object.values(next);
  };

  useImperativeHandle(ref, () => ({
    triggerSave: () => onSave(buildSaveData()),
    validate,
    getSaveData: () => buildSaveData(),
    getSnapshot: () => JSON.stringify({ categories, assessmentType }),
  }));

  const onChange = (key: string, value: unknown) => {
    setCategories(prev => ({ ...prev, [key]: value }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: '' } : prev));
  };
  const allCollapsed = shownSections.every(s => collapsed[s.name]);

  return (
    <div className="space-y-4 text-left">
      <div className="flex items-center justify-between border-b border-gray-200 pb-3">
        <h3 className="text-lg font-semibold text-gray-900">{template.title}</h3>
        <button
          type="button"
          onClick={() => setCollapsed(Object.fromEntries(shownSections.map(s => [s.name, !allCollapsed])))}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md border border-gray-300"
        >
          {allCollapsed ? <><ChevronsDown size={16} aria-hidden="true" />Expand All</> : <><ChevronsUp size={16} aria-hidden="true" />Collapse All</>}
        </button>
      </div>

      <fieldset className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <legend className="px-1 text-sm font-medium text-gray-700">Assessment type</legend>
        <div className="flex flex-wrap gap-2">
          {types.map(t => (
            <label key={t} className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${assessmentType === t ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'} ${typeLocked ? 'opacity-60' : 'cursor-pointer'}`}>
              <input type="radio" name="template-assessment-type" value={t} checked={assessmentType === t} disabled={typeLocked}
                onChange={() => setAssessmentType(t)} />
              {ASSESSMENT_TYPE_LABELS[t]}
            </label>
          ))}
        </div>
      </fieldset>

      {shownSections.map(section => (
        <TemplateSectionFields
          key={section.name}
          template={template}
          section={section}
          values={categories}
          onChange={onChange}
          errors={errors}
          readOnly={isReadOnly}
          assessmentType={assessmentType}
          collapsed={Boolean(collapsed[section.name])}
          onToggle={() => setCollapsed(prev => ({ ...prev, [section.name]: !prev[section.name] }))}
        />
      ))}
    </div>
  );
});

TemplateAssessmentForm.displayName = 'TemplateAssessmentForm';

export default TemplateAssessmentForm;
